import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useUnsavedChanges } from '@/context/UnsavedChangesContext';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ActivityLog } from '@/components/ActivityLog';
import CuttingLogCard from '@/components/jobs/CuttingLogCard';
import InstalledEquipmentCard from '@/components/jobs/InstalledEquipmentCard';
import JobExpensesCard from '@/components/jobs/JobExpensesCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Wrench, ArrowLeft, MapPin, User, FileText, CalendarIcon,
  Clock, Pencil, Users, Plus, Trash2, UserPlus, Shield,
  StickyNote, Save as SaveIcon, FolderKanban, Paperclip, Mail, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import ClickToCall from '@/components/voice/ClickToCall';
import QuickLinksBar from '@/components/common/QuickLinksBar';
import DocumentsSection from '@/components/common/DocumentsSection';
import NotesLogSection from '@/components/common/NotesLogSection';
import ClientNoteCard from '@/components/common/ClientNoteCard';
import ScopeOfWorkCard from '@/components/jobs/ScopeOfWorkCard';
import SitePhotosCard from '@/components/jobs/SitePhotosCard';
import CompletionCard from '@/components/jobs/CompletionCard';
import FiltersServiceEquipmentCard from '@/components/jobs/FiltersServiceEquipmentCard';
import InspectionDetailsCard from '@/components/jobs/InspectionDetailsCard';
import { normalizeJobCategory, jobCategoryLabel } from '@/constants/jobCategories';
import { useJobStatusConfig, statusFlowForJob, jobStatusLabel } from '@/hooks/useJobStatusConfig';
import { StatusStepper } from '@/components/shared/StatusStepper';

const TEAM_ROLE_KEYS = ['driller', 'operator', 'helper', 'supervisor'];
const OFFICE_ROLES = ['admin', 'superadmin', 'office', 'manager'];

export default function JobDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { confirmLeave } = useUnsavedChanges();
  const canEditPermitNumber = OFFICE_ROLES.includes(user?.role);
  const cuttingLogRef = useRef(null);
  const { config: jobStatusConfig } = useJobStatusConfig();
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [addingTeam, setAddingTeam] = useState(false);
  const [newMember, setNewMember] = useState({ user_id: '', role: 'helper' });
  // Prompt AC CAMBIO 2 (2026-09-21): tabs Details / Files / Notes, mismo
  // patron que el permiso. filesCount se mide aparte de DocumentsSection
  // para que el contador se vea aunque la pestaña Files no este activa.
  const [activeTab, setActiveTab] = useState('details');
  const [filesCount, setFilesCount] = useState(0);
  const [notesSaving, setNotesSaving] = useState(false);
  // Prompt AA CAMBIO 5 (2026-09-18): los OTROS trabajos del mismo proyecto,
  // leidos en vivo por project_id — Alian abre el job de oficina y necesita
  // ver que se va a hacer en obra sin abrir cada ficha.
  const [siblingJobs, setSiblingJobs] = useState([]);
  const [permitDraft, setPermitDraft] = useState('');
  const [permitSaving, setPermitSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('08:00');
  // Multi-day scheduling (Well Construction only — Alian: "start and end,
  // para que en el calendario aparezcan marcados dos o tres días").
  const [scheduleEndDate, setScheduleEndDate] = useState('');
  const [scheduleEndTime, setScheduleEndTime] = useState('17:00');
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [siteVisitOpen, setSiteVisitOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitForm, setVisitForm] = useState({ date: '', time: '08:00', note: '' });
  const [savingVisit, setSavingVisit] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const jobRes = await api.get(`/jobs/${id}`);
        setJob(jobRes.data);
        setPermitDraft(jobRes.data.permit_number || '');
        const [contactsRes, usersRes, filesRes] = await Promise.all([
          jobRes.data.customer_id ? api.get(`/contacts/${jobRes.data.customer_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          api.get('/auth/users').catch(() => ({ data: [] })),
          // Prompt AC CAMBIO 2: contador de la pestaña Files — TODOS los
          // archivos (no solo fotos), medido aparte de DocumentsSection para
          // que se vea aunque esa pestaña no este activa.
          api.get(`/files?entity_type=job&entity_id=${id}`).catch(() => ({ data: [] })),
        ]);
        setCustomer(contactsRes.data);
        setAllUsers(usersRes.data);
        setFilesCount((filesRes.data || []).length);
        // Prompt AA CAMBIO 5: jobs hermanos del mismo proyecto, en vivo.
        // El endpoint de lista no filtra por proyecto — se trae la pagina
        // amplia y se filtra aca, igual que hace PermitsPage.
        if (jobRes.data.project_id) {
          api.get('/jobs', { params: { page_size: 500 } }).then(r => {
            const all = r.data?.items || r.data || [];
            setSiblingJobs(
              all
                .filter(j => j.project_id === jobRes.data.project_id && j.id !== id)
                .sort((a, b) => String(a.job_number || '').localeCompare(String(b.job_number || ''), undefined, { numeric: true }))
            );
          }).catch(() => { /* non-blocking */ });
        } else {
          setSiblingJobs([]);
        }
      } catch {
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) return null;

  const jobCategory = normalizeJobCategory(job.type);
  const validStatuses = statusFlowForJob(jobStatusConfig, job);
  // Only Well Construction schedules a date RANGE (Filters & Service / Office
  // stay single-day — Alian: "un solo schedule está magnífico").
  const allowsDateRange = jobCategory === 'well_construction';

  const parseScheduledDate = (s) => {
    if (!s) return new Date();
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };

  const fmtLocalDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const openScheduleDialog = () => {
    setScheduleDate(job.scheduled_date ? parseScheduledDate(job.scheduled_date) : new Date());
    setScheduleTime(job.scheduled_time || '08:00');
    setScheduleEndDate(job.scheduled_end_date || '');
    setScheduleEndTime(job.scheduled_end_time || '17:00');
    setScheduleOpen(true);
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === job.status) return;
    if (newStatus === 'scheduled') {
      openScheduleDialog();
      return;
    }
    try {
      const res = await api.put(`/jobs/${job.id}`, { status: newStatus });
      setJob(res.data);
      toast.success(t('success.updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  const handleScheduleConfirm = async (override = false) => {
    if (!scheduleDate) {
      toast.error('Seleccione una fecha');
      return;
    }
    const startStr = fmtLocalDate(scheduleDate);
    if (allowsDateRange && scheduleEndDate && scheduleEndDate < startStr) {
      toast.error('La fecha de fin no puede ser anterior a la de inicio');
      return;
    }
    try {
      const res = await api.put(`/jobs/${job.id}`, {
        status: 'scheduled',
        scheduled_date: startStr,
        scheduled_time: scheduleTime,
        ...(allowsDateRange
          ? { scheduled_end_date: scheduleEndDate || '', scheduled_end_time: scheduleEndDate ? scheduleEndTime : '' }
          : {}),
        ...(override ? { override_schedule_conflict: true } : {}),
      });
      setJob(res.data);
      setScheduleOpen(false);
      setConflictOpen(false);
      toast.success(t('success.updated'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.code === 'well_construction_day_conflict') {
        if (detail.requires_confirmation) {
          setConflictInfo(detail);
          setConflictOpen(true);
        } else {
          toast.error(detail.message || 'Solo se puede agendar un pozo por día. Elija otra fecha.');
        }
        return;
      }
      toast.error((detail && detail.message) || detail || t('errors.savingError'));
    }
  };

  const openAddVisit = () => { setEditingVisit(null); setVisitForm({ date: '', time: '08:00', note: '' }); setSiteVisitOpen(true); };
  const openEditVisit = (v) => { setEditingVisit(v); setVisitForm({ date: v.date || '', time: v.time || '08:00', note: v.note || '' }); setSiteVisitOpen(true); };

  const handleSaveVisit = async () => {
    if (!visitForm.date) { toast.error('Seleccione una fecha'); return; }
    setSavingVisit(true);
    try {
      const res = editingVisit
        ? await api.put(`/jobs/${job.id}/site-visits/${editingVisit.id}`, visitForm)
        : await api.post(`/jobs/${job.id}/site-visits`, visitForm);
      setJob(res.data);
      setSiteVisitOpen(false);
      toast.success(t('success.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setSavingVisit(false);
    }
  };

  const handleDeleteVisit = async (vid) => {
    try {
      const res = await api.delete(`/jobs/${job.id}/site-visits/${vid}`);
      setJob(res.data);
      toast.success(t('success.deleted'));
    } catch {
      toast.error(t('errors.deletingError'));
    }
  };

  const handleConvertToProject = async () => {
    setConverting(true);
    try {
      const res = await api.post(`/jobs/${job.id}/convert-to-project`);
      const proj = res.data.project;
      if (res.data.created) {
        toast.success(`Proyecto ${proj.project_number} creado`);
      } else {
        toast.info(res.data.message || 'El trabajo ya está vinculado a un proyecto');
      }
      navigate(`/projects/${proj.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setConverting(false);
    }
  };

  // Prompt AA CAMBIO 7 (2026-09-18): las notas pasan a ser una LISTA de
  // entradas (mas reciente arriba, cada una con fecha y autor abajo en
  // chico). "Agregar nota" crea una entrada nueva en notes_log; el texto
  // libre historico (job.notes) NO se toca ni se migra — se muestra tal
  // cual debajo de la lista.
  const handleAddNote = async (text) => {
    setNotesSaving(true);
    try {
      const entry = {
        id: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
        text,
        author_id: user?.id || '',
        author_name: user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
        created_at: new Date().toISOString(),
      };
      const res = await api.put(`/jobs/${job.id}`, { notes_log: [...(job.notes_log || []), entry] });
      setJob(res.data);
      toast.success(t('success.updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setNotesSaving(false);
    }
  };

  // Prompt AC CAMBIO 2 (2026-09-21): "Client Note" — guarda al vuelo (onBlur),
  // independiente del resto de la ficha (no hay boton "Save" global aca).
  const handleClientNoteBlur = async () => {
    try {
      const res = await api.put(`/jobs/${job.id}`, { client_note: job.client_note || '' });
      setJob(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Prompt AD (2026-09-21): "Additional Field Notes" (Well Construction) /
  // "Work Performed / Notes" (Filters & Service) — mismo patron onBlur que
  // Client Note, pero campo separado (field_notes).
  const handleFieldNotesBlur = async () => {
    try {
      const res = await api.put(`/jobs/${job.id}`, { field_notes: job.field_notes || '' });
      setJob(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Prompt AD (2026-09-21) — Filters & Service "Recurring": guarda el
  // checkbox y la frecuencia; la GENERACION del siguiente trabajo la hace
  // el backend al completar (jobs.py update_job), nunca varios por adelantado.
  const handleRecurringToggle = async (checked) => {
    try {
      const res = await api.put(`/jobs/${job.id}`, {
        is_recurring: checked,
        recurring_frequency: checked ? (job.recurring_frequency || '1_month') : '',
      });
      setJob(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };
  const handleRecurringFrequencyChange = async (freq) => {
    try {
      const res = await api.put(`/jobs/${job.id}`, { recurring_frequency: freq });
      setJob(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Prompt AD (2026-09-21) — Filters & Service "Duplicate": clona los datos
  // basicos del trabajo en uno nuevo (sin cutting log/fotos/estado, que son
  // propios de ESTE trabajo), y navega directo a editarlo.
  const handleDuplicateJob = async () => {
    setDuplicating(true);
    try {
      const res = await api.post('/jobs', {
        project_id: job.project_id || '',
        customer_id: job.customer_id || '',
        title: job.title || '',
        description: job.description || '',
        type: job.type,
        items: job.items || [],
        well_address: job.well_address || '',
        well_city: job.well_city || '',
        well_state: job.well_state || 'FL',
        well_zip: job.well_zip || '',
        county: job.county || '',
        property_owner: job.property_owner || '',
        assigned_team: job.assigned_team || [],
      });
      toast.success('Trabajo duplicado');
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setDuplicating(false);
    }
  };

  // Prompt AA CAMBIO 6 (2026-09-18): asignar una persona a un job hermano
  // desde esta ficha — mismo control que Assigned Team (Select de usuarios +
  // PUT assigned_team), sin salir de la pantalla.
  const handleSiblingAssign = async (sibling, userId) => {
    const u = allUsers.find(x => x.id === userId);
    if (!u) return;
    const team = [...(sibling.assigned_team || [])];
    const entry = { user_id: u.id, name: u.name, role: team[0]?.role || 'helper' };
    if (team.length > 0) team[0] = entry; else team.push(entry);
    try {
      await api.put(`/jobs/${sibling.id}`, { assigned_team: team });
      setSiblingJobs(prev => prev.map(s => (s.id === sibling.id ? { ...s, assigned_team: team } : s)));
      toast.success(t('success.updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Permit Number — Alian: lo escribe SOLO la persona de oficina; para el
  // resto es un dato de solo lectura (los trabajos no se editan).
  // TEMA C1 (video "Cómo guardar material en Well Construction"): el botón
  // "Save" del encabezado antes SOLO guardaba el Permit Number, dando la
  // falsa impresión de que también guardaba el Cutting Log — el driller
  // cargaba filas, tocaba este botón y las perdía. Ahora guarda el Permit
  // Number Y el Cutting Log/Well Casing (draft vivo de CuttingLogCard, via
  // ref) en UNA sola llamada — salvo para el rol Office, a quien el backend
  // le prohíbe tocar esos campos (readOnly en CuttingLogCard, ver jobs.py).
  const handlePermitSave = async () => {
    setPermitSaving(true);
    try {
      const payload = { permit_number: permitDraft };
      const includesCuttingLog = user?.role !== 'office' && cuttingLogRef.current;
      if (includesCuttingLog) Object.assign(payload, cuttingLogRef.current.getDraft());
      const res = await api.put(`/jobs/${job.id}`, payload);
      setJob(res.data);
      if (includesCuttingLog) cuttingLogRef.current.markSaved(res.data, { silent: true });
      toast.success(includesCuttingLog ? 'Trabajo, Cutting Log y Well Casing guardados' : t('success.updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setPermitSaving(false);
    }
  };

  // Point C (Alian's video): show the assigned person's computed pay next to
  // their name — reuses the existing Settings > Users "pay_type"/"pay_value".
  // TEMA A2 (video 'Relaciones...'): Alian confirmed the base is the total
  // the client ACTUALLY pays (with the discount already applied — job.total
  // reflects that after the TEMA A1 fix), never the pre-discount list price.
  // Show the base amount too so it can be audited at a glance without a
  // calculator ("que Alian pueda auditarlo de un vistazo").
  const getCommissionDisplay = (assignedUser) => {
    if (!assignedUser) return null;
    const payType = assignedUser.pay_type;
    const payValue = parseFloat(assignedUser.pay_value) || 0;
    if (!payType || payType === 'none' || payValue <= 0) return null;
    if (payType === 'commission') {
      const base = job.total || 0;
      const amount = base * (payValue / 100);
      return {
        pct: `${payValue}%`,
        amount: `$${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
        base: `$${base.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      };
    }
    if (payType === 'per_job') {
      return { pct: null, amount: `$${payValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} (fijo)`, base: null };
    }
    return null;
  };

  return (
    // Prompt AA CAMBIO 4: en escritorio las secciones van mas compactas
    // (menos aire vertical entre tarjetas). Movil intacto (base space-y-4).
    <div data-testid="job-detail-page" className="space-y-4">
      <div className="flex items-start gap-3">
        {/* Al salir de un trabajo se vuelve a SU categoría (Work Construction),
            nunca a "All Jobs" — Alian. */}
        <Button variant="ghost" size="icon" onClick={() => { if (!confirmLeave()) return; navigate(`/jobs?type=${jobCategory}`); }} className="mt-0.5 h-9 w-9" data-testid="back-to-jobs">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span
                  data-testid="job-detail-category-badge"
                  className="text-[10px] font-semibold uppercase tracking-wide text-[#3399FF] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full"
                >
                  {jobCategoryLabel(jobCategory)}
                </span>
                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded" data-testid="job-detail-job-number">{job.job_number}</span>
                <StatusBadge status={job.status} />
                {job.source_estimate_id && (
                  <span className="text-[10px] text-violet-500 flex items-center gap-0.5 bg-violet-50 px-1.5 py-0.5 rounded">
                    <FileText className="w-2.5 h-2.5" /> {t('jobDetail.fromEstimate')}
                  </span>
                )}
              </div>
              <QuickLinksBar entityType="job" entityId={job.id} customer={customer} className="mt-2" testId="job-quick-links" />

              {/* TEMA D1 (video "Ajustes de informe para Pump Installation"):
                  Alian, repetido 5 veces — "quién es el cliente" tiene que estar
                  ARRIBA, en una sola línea, junto al teléfono/dirección/total —
                  no en una tarjeta aparte más abajo. */}
              <div data-testid="job-detail-customer-line" className="flex items-center gap-1.5 flex-wrap text-sm text-gray-700 mt-2">
                <User className="w-3.5 h-3.5 text-[#3399FF] flex-shrink-0" />
                {customer ? (
                  <>
                    <span className="font-semibold text-gray-900" data-testid="job-detail-customer-name">
                      {customer.first_name} {customer.last_name}
                    </span>
                    {customer.company_name && (
                      <span className="text-gray-500" data-testid="job-detail-customer-company">— {customer.company_name}</span>
                    )}
                    {customer.phone && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span data-testid="job-detail-customer-phone">{customer.phone}</span>
                        <ClickToCall number={customer.phone} name={`${customer.first_name || ''} ${customer.last_name || ''}`.trim()} />
                      </>
                    )}
                    {customer.email && (
                      <>
                        <span className="text-gray-300">·</span>
                        <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span data-testid="job-detail-customer-email" className="truncate">{customer.email}</span>
                      </>
                    )}
                    {job.well_address && (
                      <>
                        <span className="text-gray-300">·</span>
                        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span data-testid="job-detail-job-address">
                          {job.well_address}{job.well_city ? `, ${job.well_city}` : ''}
                        </span>
                      </>
                    )}
                    <span className="text-gray-300">·</span>
                    <span className="font-semibold text-gray-900" data-testid="job-detail-total-inline">
                      Total: ${(job.total || 0).toLocaleString()}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400 text-xs">{t('contacts.noContactSelected')}</span>
                )}
              </div>
              {/* Fix P CAMBIO 4 (2026-09-10, confirmado por Alian): condado,
                  parcela y dueño registral, compacto, junto a la dirección
                  — no una tarjeta nueva. Se leen en vivo del estimado de
                  origen (jobs.py get_job), sin backfill. */}
              {(job.county || job.parcel_id || job.property_owner) && (
                <div data-testid="job-detail-location-details" className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500 mt-1 pl-5">
                  {job.county && <span data-testid="job-detail-county">County: {job.county}</span>}
                  {job.county && (job.parcel_id || job.property_owner) && <span className="text-gray-300">·</span>}
                  {job.parcel_id && <span data-testid="job-detail-parcel">Parcel: {job.parcel_id}</span>}
                  {job.parcel_id && job.property_owner && <span className="text-gray-300">·</span>}
                  {job.property_owner && <span data-testid="job-detail-property-owner">Owner: {job.property_owner}</span>}
                </div>
              )}
            </div>

            <div className="flex flex-col items-stretch sm:items-end gap-2 self-start">
              {/* Permit Number — esquina de arriba, solo oficina lo cambia.
                  flex-wrap evita que se corte en celulares de 360-390px. */}
              <div className="flex items-center gap-1.5 flex-wrap justify-end" data-testid="job-permit-number-box">
                <Shield className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold whitespace-nowrap">Permit #</span>
                <Input
                  value={permitDraft}
                  onChange={e => setPermitDraft(e.target.value)}
                  readOnly={!canEditPermitNumber}
                  placeholder={canEditPermitNumber ? 'e.g. SAR-2026-9988' : '—'}
                  className={`h-9 sm:h-8 w-40 sm:w-40 text-sm sm:text-xs font-mono ${canEditPermitNumber ? '' : 'bg-gray-50 text-gray-600 cursor-default'}`}
                  data-testid="job-permit-number-input"
                />
                {canEditPermitNumber && (
                  <Button size="sm" variant="outline" className="h-9 sm:h-8 text-xs" onClick={handlePermitSave} disabled={permitSaving} title="Guarda el trabajo, el Cutting Log y el Well Casing" data-testid="job-permit-number-save">
                    <SaveIcon className="w-3.5 h-3.5 mr-1" /> {t('common.save', { defaultValue: 'Save' })}
                  </Button>
                )}
              </div>

              {!job.project_id && (
                <Button data-testid="convert-to-project-btn" size="sm" variant="outline" onClick={handleConvertToProject} disabled={converting} className="border-[#003366]/30 text-[#003366] hover:bg-blue-50">
                  <FolderKanban className="w-4 h-4 mr-2" /> {converting ? 'Convirtiendo…' : 'Convertir a Proyecto'}
                </Button>
              )}

              {/* Prompt AD (2026-09-21) — Filters & Service header: Recurring
                  checkbox + frecuencia, y botón Duplicate. */}
              {jobCategory === 'filters_service' && (
                <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="job-recurring-box">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!job.is_recurring}
                      onChange={e => handleRecurringToggle(e.target.checked)}
                      data-testid="job-recurring-checkbox"
                      className="w-4 h-4"
                    />
                    Recurring
                  </label>
                  {job.is_recurring && (
                    <Select value={job.recurring_frequency || '1_month'} onValueChange={handleRecurringFrequencyChange}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid="job-recurring-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1_month">Every 1 Month</SelectItem>
                        <SelectItem value="2_months">Every 2 Months</SelectItem>
                        <SelectItem value="3_months">Every 3 Months</SelectItem>
                        <SelectItem value="6_months">Every 6 Months</SelectItem>
                        <SelectItem value="12_months">Every 12 Months</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleDuplicateJob} disabled={duplicating} data-testid="job-duplicate-btn">
                    <Copy className="w-3.5 h-3.5 mr-1" /> {duplicating ? 'Duplicando…' : 'Duplicate'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Prompt AC CAMBIO 2 (2026-09-21): Details / Files / Notes, mismo
          patron que el permiso — el mapeo de que sección va en cada tipo de
          trabajo (CAMBIO 3) queda para el proximo prompt; por ahora todo lo
          que no es Files/Notes vive en Details, sin cambios de contenido. */}
      <div className="flex items-center gap-1 border-b border-gray-200" data-testid="job-detail-tabs">
        {[
          { key: 'details', icon: FileText, label: 'Details' },
          { key: 'files', icon: Paperclip, label: `Files (${filesCount})` },
          { key: 'notes', icon: StickyNote, label: `Notes (${(job.notes_log || []).length})` },
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            data-testid={`job-tab-${tab.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === tab.key ? 'border-[#003366] text-[#003366]' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'details' && (
      <div className="space-y-4" data-testid="job-tab-details-panel">
      {/* Status — stepper horizontal (Settings > Job Status). En móvil:
          scrolleable con botones grandes para cambiar de estado con el
          pulgar (Alian: "cutting log y fotos... con el pulgar"). */}
      <Card className="bg-white border-gray-100">
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold flex-shrink-0">{t('common.status', 'Status')}</span>
            <StatusStepper
              statuses={validStatuses}
              currentKey={job.status}
              onSelect={handleStatusChange}
              canEdit={true}
              testidPrefix="job-status-badge"
              listTestId="job-detail-status-badges"
              labelFn={(s) => jobStatusLabel(t, s)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Scheduling + Site Visits + Assigned Team */}
      <Card className="bg-white border-gray-100" data-testid="job-scheduling-team-card">
        <CardContent className="p-3">
          <div className={`grid grid-cols-1 gap-4 ${jobCategory !== 'permits' ? 'lg:grid-cols-2' : ''}`}>
            {jobCategory !== 'permits' && (
              <div className="space-y-3" data-testid="job-detail-schedule-card">
                <div className="flex items-center gap-2 flex-wrap">
                  <CalendarIcon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  {job.scheduled_date ? (
                    <span className="text-sm text-gray-800 font-medium" data-testid="job-scheduled-display">
                      {parseScheduledDate(job.scheduled_date).toLocaleDateString((i18n.language === 'es' ? 'es-US' : 'en-US'), { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                      {job.scheduled_time ? ` · ${job.scheduled_time}` : ''}
                      {job.scheduled_end_date && job.scheduled_end_date !== job.scheduled_date && (
                        <span data-testid="job-scheduled-end-display">
                          {' → '}
                          {parseScheduledDate(job.scheduled_end_date).toLocaleDateString((i18n.language === 'es' ? 'es-US' : 'en-US'), { weekday: 'short', month: 'short', day: 'numeric' })}
                          {job.scheduled_end_time ? ` · ${job.scheduled_end_time}` : ''}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400" data-testid="job-scheduled-empty">
                      {t('jobDetail.noScheduleSet', { defaultValue: 'Sin fecha programada' })}
                    </span>
                  )}
                  <Button data-testid="job-schedule-btn" size="sm" variant="outline" onClick={openScheduleDialog} className="ml-auto border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    <CalendarIcon className="w-4 h-4 mr-1.5" />
                    {job.scheduled_date ? t('jobDetail.reschedule', { defaultValue: 'Reprogramar' }) : t('jobDetail.schedule', { defaultValue: 'Programar fecha' })}
                  </Button>
                </div>

                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-violet-500" />
                      {t('jobDetail.siteVisitsTitle', { defaultValue: 'Site Visits' })}
                      {(job.site_visits || []).length > 0 && <span className="font-normal text-gray-400">({(job.site_visits || []).length})</span>}
                    </span>
                    <Button data-testid="add-site-visit-btn" size="sm" variant="ghost" onClick={openAddVisit} className="h-7 text-xs text-violet-700">
                      <Plus className="w-3.5 h-3.5 mr-1" /> {t('jobDetail.addSiteVisit', { defaultValue: 'Add Site Visit' })}
                    </Button>
                  </div>
                  {(job.site_visits || []).length === 0 ? (
                    <p className="text-xs text-gray-400" data-testid="no-site-visits">{t('jobDetail.noSiteVisits', { defaultValue: 'No additional site visits yet.' })}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(job.site_visits || []).map((v) => (
                        <div key={v.id} data-testid={`site-visit-${v.id}`} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-gray-50 border border-gray-100">
                          <div className="flex items-center gap-2 min-w-0">
                            <CalendarIcon className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800">
                                {parseScheduledDate(v.date).toLocaleDateString((i18n.language === 'es' ? 'es-US' : 'en-US'), { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                                {v.time ? ` · ${v.time}` : ''}
                              </p>
                              {v.note && <p className="text-[11px] text-gray-500 truncate">{v.note}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button data-testid={`edit-site-visit-${v.id}`} variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditVisit(v)}><Pencil className="w-3 h-3" /></Button>
                            <Button data-testid={`delete-site-visit-${v.id}`} variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-600" onClick={() => handleDeleteVisit(v.id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div data-testid="job-detail-team-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-violet-500" /> {t('jobDetail.assignedTeam', { count: job.assigned_team?.length || 0 })}
                </span>
              </div>

              {/* Prompt AA CAMBIO 6 (2026-09-18): cuadrito dividido a la mitad —
                  izquierda la persona asignada (nombre con el rol debajo; sin
                  asignar dice "Unassigned" en gris, nunca vacio), derecha el
                  control que ya existia (Add Member). */}
              <div className="grid grid-cols-2 rounded-lg border border-gray-200 overflow-hidden mb-2" data-testid="job-assign-split">
                <div className="p-2.5 border-r border-gray-200 min-w-0">
                  {(job.assigned_team || []).length > 0 ? (
                    <div className="space-y-2" data-testid="job-assign-split-members">
                      {job.assigned_team.map((member, i) => {
                        const resolvedName = member.name || allUsers.find(u => u.id === member.user_id)?.name || 'Sin nombre';
                        const payInfo = getCommissionDisplay(allUsers.find(u => u.id === member.user_id));
                        return (
                          <div key={i} className="flex items-center gap-2 group">
                            <div className="w-7 h-7 rounded-full bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                              <User className="w-3.5 h-3.5 text-[#003366]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 truncate" data-testid={`job-assign-split-name-${i}`}>
                                {resolvedName}
                              </p>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-[11px] text-gray-500 capitalize truncate" data-testid={`job-assign-split-role-${i}`}>
                                  {t(`jobDetail.roles.${member.role}`, { defaultValue: member.role || '' })}
                                </p>
                                {payInfo && (
                                  <span data-testid={`job-assign-split-pay-${i}`} className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded whitespace-nowrap" title={payInfo.base ? `Base: ${payInfo.base} (monto con descuento ya aplicado)` : undefined}>
                                    {payInfo.pct ? `${payInfo.pct} = ` : ''}{payInfo.amount}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button data-testid={`job-assign-split-remove-${i}`} variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" onClick={async () => {
                              const updatedTeam = job.assigned_team.filter((_, idx) => idx !== i);
                              try {
                                await api.put(`/jobs/${job.id}`, { assigned_team: updatedTeam });
                                setJob({ ...job, assigned_team: updatedTeam });
                                toast.success(t('jobDetail.memberRemoved'));
                              } catch { toast.error(t('jobDetail.removeError')); }
                            }}>
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic" data-testid="job-assign-split-unassigned">{t('jobDetail.unassigned', { defaultValue: 'Unassigned' })}</span>
                  )}
                </div>
                <div className="p-2.5 flex items-center justify-end">
                  <Button data-testid="add-team-member-btn" variant="outline" size="sm" onClick={() => setAddingTeam(!addingTeam)} className="h-8 text-xs border-[#003366]/30 text-[#003366]">
                    <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('jobDetail.addTeamMember')}
                  </Button>
                </div>
              </div>

              {addingTeam && (
                <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-blue-50/50 border border-blue-100">
                  <Select value={newMember.user_id} onValueChange={v => setNewMember({ ...newMember, user_id: v })}>
                    <SelectTrigger data-testid="team-user-select" className="h-8 text-xs flex-1 bg-white"><SelectValue placeholder={t('jobDetail.selectPerson')} /></SelectTrigger>
                    <SelectContent>
                      {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={newMember.role} onValueChange={v => setNewMember({ ...newMember, role: v })}>
                    <SelectTrigger data-testid="team-role-select" className="h-8 text-xs w-32 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEAM_ROLE_KEYS.map(r => <SelectItem key={r} value={r}>{t(`jobDetail.roles.${r}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid="confirm-add-team" size="sm" className="h-8 bg-[#003366] text-white text-xs" onClick={async () => {
                    if (!newMember.user_id) { toast.error(t('jobDetail.selectPersonRequired')); return; }
                    const userName = allUsers.find(u => u.id === newMember.user_id)?.name || '';
                    const currentTeam = job.assigned_team || [];
                    const updatedTeam = [...currentTeam, { user_id: newMember.user_id, name: userName, role: newMember.role }];
                    try {
                      await api.put(`/jobs/${job.id}`, { assigned_team: updatedTeam });
                      setJob({ ...job, assigned_team: updatedTeam });
                      setNewMember({ user_id: '', role: 'helper' });
                      setAddingTeam(false);
                      toast.success(t('jobDetail.memberAdded'));
                    } catch { toast.error(t('jobDetail.addError')); }
                  }}>
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prompt AD (2026-09-21) — Well Construction "SCOPE OF WORK": Well
          Type, Casing Size, Est. Casing/Total Depth, Screen, Gravel Pack,
          Cement/Seal + descripcion larga, con Total y comision del
          perforador al costado. */}
      {jobCategory === 'well_construction' && (
        <ScopeOfWorkCard
          job={job}
          onSaved={setJob}
          commission={getCommissionDisplay(allUsers.find(u => u.id === job.assigned_team?.[0]?.user_id))}
        />
      )}

      {/* Prompt AA CAMBIO 4 (2026-09-18): "estos cuadros estan exageradamente
          grandes... en el telefono se ve bastante bien" — en escritorio (lg+)
          las tarjetas van de a varias por fila y con menos aire; en movil
          queda EXACTAMENTE como estaba (una por fila, mismo orden). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Cutting Log — Well Construction */}
        {jobCategory === 'well_construction' && (
          <CuttingLogCard ref={cuttingLogRef} job={job} onSaved={(updated) => setJob(updated)} />
        )}

        {/* Installed Equipment — Pump Installer. Prompt AD (2026-09-21):
            Filters & Service y Site Inspection ahora tienen su propia
            plantilla (FiltersServiceEquipmentCard / InspectionDetailsCard). */}
        {jobCategory === 'pump_installer' && (
          <InstalledEquipmentCard job={job} jobCategory={jobCategory} onSaved={(updated) => setJob(updated)} />
        )}
        {jobCategory === 'filters_service' && (
          <FiltersServiceEquipmentCard job={job} onSaved={setJob} />
        )}
        {jobCategory === 'site_inspection' && (
          <InspectionDetailsCard job={job} onSaved={setJob} />
        )}

        {/* Expenses — Alian: que el trabajador pueda cargar un gasto (con
            recibo) desde el trabajo sin necesitar permiso de editarlo. */}
        <JobExpensesCard job={job} />
      </div>

      {/* Prompt AD (2026-09-21) — secciones propias por tipo: Site Photos +
          Additional Field Notes + Completion (Well Construction); Photos +
          Work Performed notes (Filters & Service / Site Inspection). */}
      {jobCategory === 'well_construction' && (
        <>
          <SitePhotosCard entityType="job" entityId={job.id} title="Site Photos" />
          <ClientNoteCard
            value={job.field_notes}
            onChange={(v) => setJob(prev => ({ ...prev, field_notes: v }))}
            onBlur={handleFieldNotesBlur}
            title="Additional Field Notes"
            icon={StickyNote}
            placeholder="Additional notes from the field..."
            testId="job-field-notes"
          />
          <CompletionCard job={job} />
        </>
      )}
      {jobCategory === 'filters_service' && (
        <ClientNoteCard
          value={job.field_notes}
          onChange={(v) => setJob(prev => ({ ...prev, field_notes: v }))}
          onBlur={handleFieldNotesBlur}
          title="Work Performed / Notes"
          icon={StickyNote}
          placeholder="Describe the work performed..."
          testId="job-field-notes"
        />
      )}
      {jobCategory === 'site_inspection' && (
        <SitePhotosCard entityType="job" entityId={job.id} title="Photos" />
      )}

      {/* Site visit add/edit dialog */}
      <Dialog open={siteVisitOpen} onOpenChange={setSiteVisitOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="site-visit-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow']">
              {editingVisit ? t('jobDetail.editSiteVisit', { defaultValue: 'Edit Site Visit' }) : t('jobDetail.addSiteVisit', { defaultValue: 'Add Site Visit' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('common.date', { defaultValue: 'Date' })}</Label>
                <Input type="date" value={visitForm.date} onChange={(e) => setVisitForm({ ...visitForm, date: e.target.value })} className="h-10" data-testid="site-visit-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('common.time', { defaultValue: 'Time' })}</Label>
                <Input type="time" value={visitForm.time} onChange={(e) => setVisitForm({ ...visitForm, time: e.target.value })} className="h-10" data-testid="site-visit-time-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('jobDetail.visitNote', { defaultValue: 'Note (optional)' })}</Label>
              <Textarea value={visitForm.note} onChange={(e) => setVisitForm({ ...visitForm, note: e.target.value })} placeholder="…" data-testid="site-visit-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSiteVisitOpen(false)}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
            <Button onClick={handleSaveVisit} disabled={savingVisit} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="site-visit-save-btn">
              {savingVisit ? t('common.loading', { defaultValue: 'Guardando…' }) : t('common.save', { defaultValue: 'Guardar' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule date/time dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="job-detail-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                <CalendarIcon className="w-4 h-4 text-white" />
              </div>
              {t('jobDetail.scheduleJob', { defaultValue: 'Programar Trabajo' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                {allowsDateRange ? 'Start Date' : t('jobDetail.date', { defaultValue: 'Fecha' })}
              </Label>
              <div className="border rounded-lg overflow-hidden">
                <Calendar mode="single" selected={scheduleDate} onSelect={setScheduleDate} className="mx-auto" data-testid="job-schedule-calendar" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                {allowsDateRange ? 'Start Time' : t('jobDetail.time', { defaultValue: 'Hora' })}
              </Label>
              <Input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} className="h-10" data-testid="job-schedule-time-input" />
            </div>
            {allowsDateRange && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100" data-testid="job-schedule-range-fields">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">End Date</Label>
                  <Input
                    type="date"
                    value={scheduleEndDate}
                    min={scheduleDate ? fmtLocalDate(scheduleDate) : undefined}
                    onChange={e => setScheduleEndDate(e.target.value)}
                    className="h-10"
                    data-testid="job-schedule-end-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-gray-400" /> End Time
                  </Label>
                  <Input type="time" value={scheduleEndTime} onChange={e => setScheduleEndTime(e.target.value)} className="h-10" data-testid="job-schedule-end-time-input" />
                </div>
                <p className="col-span-2 text-[11px] text-gray-400">
                  Deje End Date vacío para un trabajo de un solo día. Con rango, el trabajo se marca en todos los días en el calendario.
                </p>
              </div>
            )}
            {scheduleDate && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center gap-3" data-testid="job-schedule-summary">
                <CalendarIcon className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-indigo-900">
                    {scheduleDate.toLocaleDateString((i18n.language === 'es' ? 'es-US' : 'en-US'), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    {allowsDateRange && scheduleEndDate && scheduleEndDate !== fmtLocalDate(scheduleDate)
                      ? ` → ${parseScheduledDate(scheduleEndDate).toLocaleDateString((i18n.language === 'es' ? 'es-US' : 'en-US'), { weekday: 'long', month: 'long', day: 'numeric' })}`
                      : ''}
                  </p>
                  <p className="text-xs text-indigo-600">
                    {scheduleTime ? `a las ${scheduleTime}` : 'Sin hora definida'}
                    {allowsDateRange && scheduleEndDate ? ` — termina ${scheduleEndTime}` : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
            <Button onClick={() => handleScheduleConfirm(false)} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="job-schedule-confirm-btn">
              <CalendarIcon className="w-4 h-4 mr-1.5" />
              {t('jobDetail.schedule', { defaultValue: 'Programar' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Well Construction capacity confirmation (admin only) */}
      <Dialog open={conflictOpen} onOpenChange={setConflictOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="job-schedule-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <CalendarIcon className="w-4 h-4 text-amber-600" />
              </div>
              Ya hay un pozo agendado ese día
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2" data-testid="job-schedule-conflict-message">
            {conflictInfo?.message || 'Ya existe un Well Construction agendado para ese día. ¿Desea agendar otro de todas formas?'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflictOpen(false)} data-testid="job-schedule-conflict-cancel-btn">Cancelar</Button>
            <Button onClick={() => handleScheduleConfirm(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="job-schedule-conflict-confirm-btn">
              Sí, agendar igual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prompt AA CAMBIO 4/5/6: en escritorio, jobs hermanos y descripcion
          uno al lado del otro; en movil, apilados igual que antes. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* CAMBIO 5 — los otros trabajos del mismo proyecto, en vivo por
            project_id (sin campos nuevos ni migraciones), cada uno con su
            numero, tipo y descripcion en solo lectura. CAMBIO 6 — cada
            cuadrito dividido: izquierda la persona asignada (nombre con el
            rol debajo; "Unassigned" en gris si no hay), derecha el control
            Assign (mismo Select de usuarios + PUT assigned_team de siempre). */}
        {siblingJobs.length > 0 && (
          <Card className="bg-white border-gray-100 shadow-sm" data-testid="job-sibling-jobs-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <Wrench className="w-4 h-4 text-violet-500" /> {t('jobDetail.projectJobs', { defaultValue: 'Project Jobs' })}
                <span className="text-xs font-normal text-gray-400">({siblingJobs.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 space-y-2.5">
              {siblingJobs.map(sib => {
                const sibItems = sib.items || [];
                const desc = sibItems
                  .map(it => (it.description && it.description !== it.name
                    ? `${it.name || ''} — ${it.description}`.replace(/^ — /, '')
                    : (it.name || it.description || '')))
                  .filter(Boolean)
                  .join(' · ') || (sib.description || '');
                const assignee = (sib.assigned_team || [])[0];
                const assigneeName = assignee ? (assignee.name || allUsers.find(u => u.id === assignee.user_id)?.name || '') : '';
                return (
                  <div key={sib.id} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`sibling-job-${sib.id}`}>
                    <button type="button" onClick={() => navigate(`/jobs/${sib.id}`)} className="w-full text-left px-3 pt-2 pb-1.5 hover:bg-gray-50 transition-colors" data-testid={`sibling-job-link-${sib.id}`}>
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono font-bold text-[#003366]">{sib.job_number}</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full">{jobCategoryLabel(normalizeJobCategory(sib.type))}</span>
                        <StatusBadge status={sib.status} />
                      </span>
                      {desc && <p className="text-xs text-gray-600 mt-1 line-clamp-2" data-testid={`sibling-job-desc-${sib.id}`}>{desc}</p>}
                    </button>
                    <div className="grid grid-cols-2 border-t border-gray-200">
                      <div className="px-3 py-2 border-r border-gray-200 flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-[#003366]" />
                        </div>
                        {assigneeName ? (
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-900 truncate" data-testid={`sibling-job-assignee-${sib.id}`}>{assigneeName}</p>
                            <p className="text-[10px] text-gray-500 capitalize truncate">{t(`jobDetail.roles.${assignee?.role}`, { defaultValue: assignee?.role || '' })}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic" data-testid={`sibling-job-assignee-${sib.id}`}>{t('jobDetail.unassigned', { defaultValue: 'Unassigned' })}</span>
                        )}
                      </div>
                      <div className="px-2 py-1.5 flex items-center justify-end">
                        <Select value={assignee?.user_id || ''} onValueChange={(v) => handleSiblingAssign(sib, v)}>
                          <SelectTrigger className="h-7 text-[11px] w-auto min-w-[92px] bg-white" data-testid={`sibling-job-assign-select-${sib.id}`}>
                            <SelectValue placeholder={t('jobDetail.assign', { defaultValue: 'Assign' })} />
                          </SelectTrigger>
                          <SelectContent>
                            {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Fix P CAMBIO 4 (2026-09-10, confirmado por Alian): esto es la orden
            de trabajo — el perforador necesita el texto COMPLETO del item
            (casing, sellado, regulaciones, que no incluye), no solo el
            encabezado corto que dejó TEMA A4. job.items ya trae la
            descripción completa desde la conversión (copiada verbatim del
            invoice/estimado) — esto es puro cambio de RENDER, no hace falta
            tocar ningún dato. */}
        {(job.items || []).length > 0 && (
          <Card className="bg-white border-gray-100 shadow-sm" data-testid="job-items-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <Wrench className="w-4 h-4 text-[#3399FF]" /> {t('jobDetail.description')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {job.items.map((item, i) => {
                const headline = item.name || item.description || '';
                const fullDescription = (item.description || '').trim() && item.description !== headline ? item.description : '';
                return (
                  <div key={i} data-testid={`job-item-summary-${i}`} className="pb-2 border-b border-gray-50 last:border-0 last:pb-0">
                    <p className="text-sm text-gray-800 font-medium">
                      {headline}{item.quantity ? ` — ${item.quantity} ${item.unit || ''}`.replace(/\s+$/, '') : ''}
                    </p>
                    {fullDescription && (
                      <p className="text-xs text-gray-500 mt-0.5" data-testid={`job-item-full-description-${i}`}>{fullDescription}</p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Activity Log (read-only) */}
      <ActivityLog entityType="jobs" entityId={id} />
      </div>
      )}

      {activeTab === 'files' && (
        <div data-testid="job-tab-files-panel">
          <DocumentsSection entityType="job" entityId={job.id} onCountChange={setFilesCount} />
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="space-y-3" data-testid="job-tab-notes-panel">
          <NotesLogSection
            notesLog={job.notes_log || []}
            onAddNote={handleAddNote}
            saving={notesSaving}
            testIdPrefix="job-notes"
            extraBlock={(job.source_invoice_notes || job.source_estimate_notes) && (
              <div className="space-y-2 mb-3 pb-3 border-b border-gray-100" data-testid="job-source-notes-block">
                {job.source_invoice_notes && (
                  <div data-testid="job-source-notes-invoice">
                    <p className="text-[10px] uppercase tracking-wide text-violet-500 font-semibold">
                      {t('jobDetail.fromInvoiceNotes', { number: job.source_invoice_number, defaultValue: `From Invoice ${job.source_invoice_number || ''}` })}
                    </p>
                    <p className="text-sm text-gray-700 border-l-2 border-violet-300 pl-2.5 py-0.5 mt-1">{job.source_invoice_notes}</p>
                  </div>
                )}
                {job.source_estimate_notes && (
                  <div data-testid="job-source-notes-estimate">
                    <p className="text-[10px] uppercase tracking-wide text-blue-500 font-semibold">
                      {t('jobDetail.fromEstimateNotes', { number: job.source_estimate_number, defaultValue: `From Estimate ${job.source_estimate_number || ''}` })}
                    </p>
                    <p className="text-sm text-gray-700 border-l-2 border-blue-300 pl-2.5 py-0.5 mt-1">{job.source_estimate_notes}</p>
                  </div>
                )}
              </div>
            )}
          />
          {(job.notes || '').trim() && (
            <Card className="bg-white border-gray-100" data-testid="job-notes-legacy">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">{t('jobDetail.earlierNotes', { defaultValue: 'Earlier notes' })}</p>
                {(job.notes || '').split('\n').filter(l => l.trim()).map((line, i) => (
                  <p key={i} className="text-sm text-gray-700 border-l-2 border-amber-200 pl-2.5 py-0.5">{line}</p>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Prompt AC CAMBIO 2 (2026-09-21): "Client Note" — siempre visible,
          fuera de las 3 pestañas, guarda al vuelo (onBlur). */}
      <ClientNoteCard
        value={job.client_note}
        onChange={(v) => setJob(prev => ({ ...prev, client_note: v }))}
        onBlur={handleClientNoteBlur}
        placeholder="Add a note..."
        testId="job-client-note"
      />
    </div>
  );
}
